import { HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DeviceLocalData } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import jwt_decode from 'jwt-decode';
import { Observable } from 'rxjs';
import { FILE_ENDPOINTS, IS_EXTERNAL_KEYCLOAK } from './http-constants';
import { KeycloakWrapperService } from './services/keycloak-wrapper-service/keycloak-wrapper.service';
import { ProfileState } from './state/user-profile.state';

export const BYPASS_AUTH_INTERCEPTOR = new HttpContextToken<boolean>(() => false);

@Injectable()
export class httpInterceptor implements HttpInterceptor {
  private readonly store = inject(Store);
  private readonly keycloakService = inject(KeycloakWrapperService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const keycloakToken = this.keycloakService.getKeycloakInstance()?.token;
    const localStorageToken = sessionStorage.getItem('token');

    let updatedToken: string | undefined = keycloakToken
      ? `Bearer ${keycloakToken}`
      : localStorageToken
        ? `Bearer ${localStorageToken}`
        : undefined;

    if (updatedToken === undefined) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).accessToken) {
        console.log(`updatedToken is undefined and fallback to window token and proceed`);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updatedToken = `Bearer ${(window as any).accessToken}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        sessionStorage.setItem('token', (window as any).accessToken);
      } else {
        console.warn(`updatedToken is undefined and no fallback window token so proceeding`);
      }
    }

    if (req.context.get(BYPASS_AUTH_INTERCEPTOR)) {
      updatedToken = undefined;
    }

    if (req.context.get(IS_EXTERNAL_KEYCLOAK)) {
      // For external keycloak requests, only include essential headers otherwise may be blocked by cors policies
      const simpleReq: HttpRequest<any> = req.clone({
        setHeaders: updatedToken
          ? {
              Authorization: updatedToken,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            }
          : {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
      });
      return next.handle(simpleReq);
    }
    const deviceInfo = localStorage.getItem('deviceInfo');
    let deviceData: DeviceLocalData;
    try {
      deviceData = JSON.parse(deviceInfo ?? '{}');
    } catch (error) {
      deviceData = {} as DeviceLocalData;
    }
    const deviceManufacturer = deviceData?.manufacturer ?? '';
    const deviceOS = deviceData?.os ?? '';
    const deviceModel = deviceData?.deviceModel ?? '';
    const deviceId = deviceData?.deviceId ?? '';
    const profileData = this.store.selectSnapshot(ProfileState.getProfile);

    // Allow requests to override org id to restore user's last selected organization on login.
    // On initial page load, profile state is not yet initialized but localStorage contains
    // the user's preference. getUser() sets this header to ensure the backend returns data
    // for the correct organization, which then initializes the profile state properly.
    const existingOrgId = req.headers.get('X-Finxone-Org-Id');

    // Only send org header if org id is present in token
    // This prevents issues where org id is sent but not valid for the user
    // i.e. 403 errors from the API
    // empty is the default and won't get enforced by the API
    let organisationId = '';
    if (existingOrgId) {
      // Use the org id from the request headers if already set
      organisationId = existingOrgId;
    } else {
      // Otherwise use the one from profile state
      const organisationsInToken = extractOrgIdsFromToken(updatedToken ?? '');
      if (organisationsInToken.includes(profileData?.activeOrganisationId ?? '')) {
        organisationId = profileData?.activeOrganisationId ?? '';
      }
    }

    const role = profileData?.activeRole ?? '';
    let authHeaderValue = undefined;

    if (
      new RegExp(window.location.hostname).exec(req.url) ||
      /finxone.com/.exec(req.url) ||
      /localhost/.exec(req.url)
    ) {
      authHeaderValue = updatedToken
        ? {
            Authorization: updatedToken,
          }
        : {};
    } else {
      authHeaderValue = {};
    }

    const body = req.body;
    let otpValue: string | undefined;

    if (body && 'otp' in body) {
      otpValue = body.otp; // Extract the OTP from the body
      delete body.otp; // Remove OTP from the body
    }

    const commonHeaders = {
      ...authHeaderValue,
      'X-Finxone-Org-Id': organisationId,
      'X-Finxone-Role': role,
      'X-Finxone-User-Agent': window.navigator.userAgent,
      'X-Finxone-Device-Id': deviceId,
      'X-Finxone-Device-Manufacture': deviceManufacturer,
      'X-Finxone-Device-Os': deviceOS,
      'X-Finxone-Device-Model': deviceModel,
    };
    const isFileRequest: boolean = FILE_ENDPOINTS.some((endpoint) => req.url.includes(endpoint));

    const headers = {
      ...commonHeaders,
      ...(isFileRequest
        ? {
            Accept: '*/*',
          }
        : {
            Accept: 'application/json',
            'Content-Type': 'application/json; charset=utf-8',
            ...(otpValue ? { 'X-Finxone-otp': otpValue } : {}),
          }),
    };

    return next.handle(req.clone({ setHeaders: headers }));
  }
}

// Should only be used here in the http interceptor
// will take the bearer token and extract the org ids from it
function extractOrgIdsFromToken(token: string): string[] {
  if (token === '') {
    return [];
  }

  const auth = token.split(' ');

  // We only allow bearer
  if (auth[0].toLowerCase() !== 'bearer') {
    console.error(`Only Bearer authentication is allowed in extractOrgIdsFromToken: ${token}`);
    return [];
  }

  try {
    const decodedToken: {
      organisation: { [orgId: string]: { roles: string[]; name: string } };
    } = jwt_decode(token);
    return Object.keys(decodedToken.organisation);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return [];
  }
}
